export const ANALYTICS_IDLE_TIMEOUT_MS = 5_000;
export const ANALYTICS_FALLBACK_DELAY_MS = 3_000;

type AnalyticsWindow = Pick<
  Window,
  | "addEventListener"
  | "removeEventListener"
  | "setTimeout"
  | "clearTimeout"
> & {
  requestIdleCallback?: Window["requestIdleCallback"];
  cancelIdleCallback?: Window["cancelIdleCallback"];
};

/**
 * Keep non-essential analytics work behind the browser's load event, then use
 * idle time when it is available. The timeout is deliberately attached after
 * load so analytics cannot compete with hydration or critical subresources.
 */
export function scheduleAnalyticsAfterLoad(
  callback: () => void,
  browserWindow: AnalyticsWindow = window,
  browserDocument: Pick<Document, "readyState"> = document,
): () => void {
  let cancelled = false;
  let listeningForLoad = false;
  let idleHandle: number | null = null;
  let timeoutHandle: number | null = null;

  const run = () => {
    if (cancelled) return;
    callback();
  };

  const scheduleIdleWork = () => {
    listeningForLoad = false;
    if (cancelled) return;

    if (typeof browserWindow.requestIdleCallback === "function") {
      idleHandle = browserWindow.requestIdleCallback(run, {
        timeout: ANALYTICS_IDLE_TIMEOUT_MS,
      });
      return;
    }

    timeoutHandle = browserWindow.setTimeout(
      run,
      ANALYTICS_FALLBACK_DELAY_MS,
    );
  };

  if (browserDocument.readyState === "complete") {
    scheduleIdleWork();
  } else {
    listeningForLoad = true;
    browserWindow.addEventListener("load", scheduleIdleWork, { once: true });
  }

  return () => {
    cancelled = true;
    if (listeningForLoad) {
      browserWindow.removeEventListener("load", scheduleIdleWork);
    }
    if (
      idleHandle !== null &&
      typeof browserWindow.cancelIdleCallback === "function"
    ) {
      browserWindow.cancelIdleCallback(idleHandle);
    }
    if (timeoutHandle !== null) {
      browserWindow.clearTimeout(timeoutHandle);
    }
  };
}
